import * as IpAddress from 'ip-address'
import { without } from 'ramda'
import { customValidator } from 'core/utils/fieldValidators'
import { isValidIpv4Address, isValidIpv4Cidr } from 'core/utils/validators'

export const getPhysicalNetworkInterfaceLabel = (
  physicalNetworksInterfaceMap,
  vmPhysicalNetwork,
) => {
  return physicalNetworksInterfaceMap?.find((network) => network.interface === vmPhysicalNetwork)
    ?.label
}

export const getPhysicalNetworkInterface = (physicalNetworksInterfaceMap, label) => {
  return physicalNetworksInterfaceMap?.find((network) => network.label === label)?.interface
}

export const getPhysicalNetworkInterfaceIndex = (physicalNetworksInterfaceMap, interfaceName) => {
  return physicalNetworksInterfaceMap?.findIndex((network) => network.interface === interfaceName)
}

export const getNetworkBody = ({ networkType, params, physicalNetworksInterfaceMap }) => {
  if (networkType === 'flat') {
    return {
      network: {
        name: params.vmNetworkName,
        description: 'The default VM network',
        admin_state_up: true,
        'provider:network_type': params.networkType,
        'provider:physical_network': getPhysicalNetworkInterfaceLabel(
          physicalNetworksInterfaceMap,
          params.vmNetworkPhysNet,
        ),
        'router:external': !!params.external,
        shared: !!params.shared,
      },
    }
  } else if (networkType === 'vlan') {
    return {
      network: {
        name: params.vmNetworkName,
        description: 'The default VM network',
        admin_state_up: true,
        'provider:network_type': params.networkType,
        'provider:physical_network': getPhysicalNetworkInterfaceLabel(
          physicalNetworksInterfaceMap,
          params.vmNetworkPhysNet,
        ),
        'provider:segmentation_id': params.segmentationId,
        'router:external': !!params.external,
        shared: !!params.shared,
      },
    }
  } else if (networkType === 'vxlan') {
    return {
      network: {
        name: params.vmNetworkName,
        description: 'The default VM network',
        admin_state_up: true,
        'provider:network_type': params.networkType,
        'provider:segmentation_id': params.segmentationId,
        'router:external': !!params.external,
        shared: !!params.shared,
      },
    }
  }
  return {}
}

export const cidrValidator = customValidator((value) => {
  if (!value) {
    return false
  }
  return isValidIpv4Cidr(value)
}, 'Invalid IPv4 CIDR provided')

export const gatewayInCidrValidator = customValidator((value, formValues) => {
  if (!value) {
    return true
  }
  try {
    const testCidr = new IpAddress.Address4(formValues.cidr)
    return new IpAddress.Address4(value).isInSubnet(testCidr)
  } catch (err) {
    return false
  }
}, 'Gateway IP address is not inside of designated CIDR')

export const gatewayIpValid = customValidator((value, formValues) => {
  if (!value) {
    return true
  }
  return isValidIpv4Address(value)
}, 'Gateway IP address is an invalid IP address')

export const waitSeconds = async (seconds) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve('')
    }, seconds * 1000)
  })
}

export const rangeLabelMap = {
  vlan: 'VLAN Range',
  vxlan: 'VXLAN VNID Range',
  geneve: 'Geneve Tunnel ID Range',
}

export const rangeTooltipMap = {
  vlan: 'You can specify the VLAN range. Each range must follow the min-max format (eg. 50-60). The valid VLAN ID range is 2 to 4094. Individual VLANs are not supported.',
  vxlan:
    'You can specify the VXLAN VNIDs or VNID range. Each range must follow the min-max format (eg. 3050-3060). The valid VNID range is 1 to 4095. Individual VNIDs are not supported.',
  geneve:
    'You can specify the ID range. Each range must follow the min-max format (eg. 6050-6060). The valid ID range is 1 to 65535. Individual IDs are not supported.',
}

export const validSegmentationRange = {
  vlan: '2-4094',
  vxlan: '1-4095',
  geneve: '1-65535',
}

export const rangePlaceholderMap = {
  vlan: 'eg. 50-60',
  vxlan: 'eg. 3050-3060',
  geneve: 'eg. 50-60',
}

export const allSystemTrafficSelected = (hostConfigs) => {
  for (const config of hostConfigs) {
    const condsFulfilled = config?.networkInterfaces?.reduce(
      (accum, iface) => {
        return {
          management: accum.management || iface.management,
          vmConsole: accum.vmConsole || iface.vmConsole,
          imageLibrary: accum.imageLibrary || iface.imageLibrary,
          tunnel: accum.tunnel || iface.tunnel,
          hostLiveness: accum.hostLiveness || iface.hostLiveness,
        }
      },
      {
        management: false,
        vmConsole: false,
        imageLibrary: false,
        tunnel: false,
        hostLiveness: false,
      },
    )
    if (
      !condsFulfilled.management ||
      !condsFulfilled.vmConsole ||
      !condsFulfilled.imageLibrary ||
      !condsFulfilled.tunnel ||
      !condsFulfilled.hostLiveness
    ) {
      return false
    }
  }
  return true
}

const DEFAULT_VOLUMES_DIR = '/opt/pf9/etc/pf9-cindervolume-base/volumes/'

export const GET_DRIVER_CONFIG = {
  NFS: {
    keyValues: [
      {
        key: 'nfs_shares_config',
        value: '/opt/pf9/etc/pf9-cindervolume-base/conf.d/nfs_shares',
        required: true,
        tooltip:
          'If you set a custom nfs_shares_config path, make sure it’s owned by pf9:pf9group. Otherwise, the Persistent Storage role will fail to converge.',
      },
      { key: 'nfs_mount_points', value: '' },
      { key: 'nfs_snapshot_support', value: 'true' },
      { key: 'nas_secure_file_permissions', value: 'false' },
      { key: 'nas_secure_file_operations', value: 'false' },
      { key: 'nfs_mount_point_base', value: DEFAULT_VOLUMES_DIR },
      // Remove these until backend NFS is fixed
      // { key: 'image_volume_cache_enabled', value: 'true' },
      // { key: 'image_volume_cache_max_size_gb', value: '200' },
      // { key: 'image_volume_cache_max_count', value: '50' },
    ],
  },
  LVM: {
    keyValues: [
      { key: 'volume_group', value: 'cinder-volumes' },
      { key: 'target_ip_address', value: '' },
      { key: 'target_protocol', value: 'iscsi' },
      { key: 'lvm_type', value: 'default' },
      { key: 'volumes_dir', value: DEFAULT_VOLUMES_DIR },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'image_volume_cache_max_count', value: '50' },
    ],
  },
  NetAppONTAPNFS: {
    keyValues: [
      { key: 'netapp_storage_protocol', value: 'nfs', required: true, disabled: true },
      { key: 'netapp_storage_family', value: 'ontap_cluster', required: true },
      { key: 'netapp_transport_type', value: 'https', required: true },
      { key: 'netapp_server_hostname', value: '', required: true },
      { key: 'netapp_login', value: 'vsadmin', required: true },
      { key: 'netapp_password', value: '', type: 'password', required: true },
      { key: 'netapp_vserver', value: '', required: true },
      {
        key: 'nfs_shares_config',
        value: '/opt/pf9/etc/pf9-cindervolume-base/conf.d/nfs_shares',
        required: true,
      },
      { key: 'volumes_dir', value: '/opt/pf9/etc/pf9-cindervolume-base/volumes/' },
      { key: 'nfs_mount_points', value: '' },
      { key: 'netapp_server_port', value: '443' },
      { key: 'netapp_ssl_cert_path', value: '' },
      { key: 'netapp_certificate_host_validation', value: 'false' },
      { key: 'nas_secure_file_operations', value: 'false' },
      { key: 'nas_secure_file_permissions', value: 'false' },
      { key: 'nfs_mount_options', value: '' },
      { key: 'nfs_snapshot_support', value: 'true' },
      { key: 'reserved_percentage', value: '10' },
      { key: 'max_over_subscription_ratio', value: '20' },
      { key: 'image_volume_cache_enabled', value: 'false' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'netapp_performance_cache_expiry_duration', value: '600' },
      { key: 'netapp_dedupe_cache_expiry_duration', value: '600' },
    ],
  },
  NetAppONTAPISCSI: {
    keyValues: [
      { key: 'netapp_storage_protocol', value: 'iscsi', required: true, disabled: true },
      { key: 'netapp_storage_family', value: 'ontap_cluster', required: true },
      { key: 'netapp_transport_type', value: 'https', required: true },
      { key: 'netapp_server_hostname', value: '', required: true },
      { key: 'netapp_login', value: 'vsadmin', required: true },
      { key: 'netapp_password', value: '', type: 'password', required: true },
      { key: 'netapp_vserver', value: '', required: true },
      { key: 'netapp_server_port', value: '443' },
      { key: 'netapp_certificate_host_validation', value: 'false' },
      { key: 'netapp_pool_name_search_pattern', value: '' },
      { key: 'netapp_lun_space_reservation', value: 'disabled' },
      { key: 'netapp_ssl_cert_path', value: '' },
      { key: 'netapp_use_legacy_client', value: 'true' },
      { key: 'netapp_driver_reports_provisioned_capacity', value: 'true' },
      { key: 'reserved_percentage', value: '20' },
      { key: 'max_over_subscription_ratio', value: '1.0' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'false' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'netapp_performance_cache_expiry_duration', value: '600' },
      { key: 'netapp_dedupe_cache_expiry_duration', value: '600' },
    ],
  },
  NetAppONTAPFC: {
    keyValues: [
      { key: 'netapp_storage_protocol', value: 'fc', required: true, disabled: true },
      { key: 'netapp_storage_family', value: 'ontap_cluster', required: true },
      { key: 'netapp_transport_type', value: 'https', required: true },
      { key: 'netapp_server_hostname', value: '', required: true },
      { key: 'netapp_login', value: 'vsadmin', required: true },
      { key: 'netapp_password', value: '', type: 'password', required: true },
      { key: 'netapp_vserver', value: '', required: true },
      { key: 'netapp_server_port', value: '443' },
      { key: 'netapp_certificate_host_validation', value: 'false' },
      { key: 'netapp_pool_name_search_pattern', value: '' },
      { key: 'netapp_lun_space_reservation', value: 'disabled' },
      { key: 'netapp_ssl_cert_path', value: '' },
      { key: 'netapp_use_legacy_client', value: 'true' },
      { key: 'netapp_driver_reports_provisioned_capacity', value: 'true' },
      { key: 'reserved_percentage', value: '10' },
      { key: 'max_over_subscription_ratio', value: '20' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'false' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'netapp_performance_cache_expiry_duration', value: '600' },
      { key: 'netapp_dedupe_cache_expiry_duration', value: '600' },
    ],
  },
  NetAppONTAPNVMe: {
    keyValues: [
      { key: 'netapp_storage_protocol', value: 'nvme', required: true, disabled: true },
      { key: 'netapp_storage_family', value: 'ontap_cluster', required: true },
      { key: 'netapp_transport_type', value: 'https', required: true },
      { key: 'netapp_server_hostname', value: '', required: true },
      { key: 'netapp_login', value: 'vsadmin', required: true },
      { key: 'netapp_password', value: '', type: 'password', required: true },
      { key: 'netapp_vserver', value: '', required: true },
      { key: 'netapp_server_port', value: '443' },
      { key: 'netapp_use_legacy_client', value: 'false' },
      { key: 'netapp_certificate_host_validation', value: 'false' },
      { key: 'netapp_ssl_cert_path', value: '' },
      { key: 'netapp_pool_name_search_pattern', value: '' },
      { key: 'netapp_lun_space_reservation', value: 'disabled' },
      { key: 'netapp_driver_reports_provisioned_capacity', value: 'true' },
      { key: 'reserved_percentage', value: '10' },
      { key: 'max_over_subscription_ratio', value: '20' },
      { key: 'image_volume_cache_enabled', value: 'false' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '500' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
    ],
  },
  PureStorageISCSI: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'pure_api_token', value: '', required: true, type: 'password' },
      { key: 'pure_iscsi_cidr', value: '0.0.0.0/0' },
      { key: 'pure_iscsi_cidr_list', value: '' },
      { key: 'pure_automatic_max_oversubscription_ratio', value: 'true' },
      { key: 'driver_ssl_cert_path', value: '' },
      { key: 'suppress_requests_ssl_warnings', value: 'true' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
    ],
  },
  PureStorageFC: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'pure_api_token', value: '', required: true, type: 'password' },
      { key: 'pure_automatic_max_oversubscription_ratio', value: 'true' },
      { key: 'driver_ssl_cert_path', value: 'None' },
      { key: 'suppress_requests_ssl_warnings', value: 'true' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20.0' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
      { key: 'driver_ssl_cert_verify', value: 'false' },
      { key: 'pure_eradicate_on_delete', value: 'false' },
      { key: 'pure_host_personality', value: 'None' },
      { key: 'pure_replica_interval_default', value: '3600' },
      { key: 'pure_replica_retention_long_term_default', value: '7' },
      { key: 'pure_replica_retention_long_term_per_day_default', value: '3' },
      { key: 'pure_replica_retention_short_term_default', value: '14400' },
      { key: 'pure_replication_pg_name', value: 'cinder-group' },
      { key: 'pure_replication_pod_name', value: 'cinder-pod' },
      { key: 'pure_trisync_enabled', value: 'false' },
      { key: 'pure_trisync_pg_name', value: 'cinder-trisync' },
      { key: 'replication_device', value: 'None' },
      { key: 'use_chap_auth', value: 'false' },
    ],
  },
  PureStorageNVME: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'pure_api_token', value: '', required: true, type: 'password' },
      { key: 'pure_nvme_cidr', value: '0.0.0.0/0' },
      { key: 'pure_nvme_cidr_list', value: '' },
      { key: 'pure_nvme_transport', value: 'roce' },
      { key: 'pure_automatic_max_oversubscription_ratio', value: 'true' },
      { key: 'driver_ssl_cert_path', value: '' },
      { key: 'suppress_requests_ssl_warnings', value: 'true' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20.0' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
      { key: 'driver_ssl_cert_verify', value: 'false' },
      { key: 'pure_eradicate_on_delete', value: 'false' },
      { key: 'pure_host_personality', value: 'None' },
      { key: 'use_chap_auth', value: 'false' },
    ],
  },
  HitachiISCSI: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', required: true, type: 'password' },
      { key: 'hitachi_storage_id', value: '', required: true },
      { key: 'hitachi_pools', value: '', required: true },
      { key: 'hitachi_target_ports', value: '', required: true },
      { key: 'hitachi_compute_target_ports', value: '' },
      { key: 'hitachi_group_create', value: 'true' },
      { key: 'hitachi_copy_speed', value: '3' },
      { key: 'hitachi_ldev_range', value: '' },
      { key: 'hitachi_extend_snapshot_volumes', value: 'true' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
    ],
  },
  HitachiFC: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', required: true, type: 'password' },
      { key: 'hitachi_storage_id', value: '', required: true },
      { key: 'hitachi_pools', value: '', required: true },
      { key: 'hitachi_target_ports', value: '', required: true },
      { key: 'hitachi_compute_target_ports', value: '' },
      { key: 'hitachi_group_create', value: 'true' },
      { key: 'hitachi_copy_speed', value: '3' },
      { key: 'hitachi_ldev_range', value: '' },
      { key: 'hitachi_extend_snapshot_volumes', value: 'true' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
    ],
  },
  InfinidatISCSI: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', required: true, type: 'password' },
      { key: 'infinidat_pool_name', value: '', required: true },
      { key: 'infinidat_iscsi_netspaces', value: '', required: true },
      { key: 'infinidat_storage_protocol', value: 'iscsi', required: true, disabled: true },
      { key: 'san_thin_provision', value: 'true' },
      { key: 'infinidat_use_compression', value: 'false' },
      { key: 'driver_use_ssl', value: 'false' },
      { key: 'suppress_requests_ssl_warnings', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
    ],
  },
  InfinidatFC: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', required: true, type: 'password' },
      { key: 'infinidat_pool_name', value: '', required: true },
      { key: 'infinidat_storage_protocol', value: 'fc', required: true, disabled: true },
      { key: 'san_thin_provision', value: 'true' },
      { key: 'infinidat_use_compression', value: 'false' },
      { key: 'driver_use_ssl', value: 'false' },
      { key: 'suppress_requests_ssl_warnings', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
    ],
  },
  HuaweiISCSI: {
    keyValues: [
      { key: 'cinder_huawei_conf_file', value: '/etc/cinder/cinder_huawei_conf.xml' },
      { key: 'hypermetro_devices', value: '' },
      { key: 'metro_domain_name', value: '' },
      { key: 'metro_san_address', value: '' },
      { key: 'metro_san_password', value: '', type: 'password' },
      { key: 'metro_san_user', value: '' },
      { key: 'metro_storage_pools', value: '' },
      { key: 'volumes_dir', value: DEFAULT_VOLUMES_DIR },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'image_volume_cache_max_count', value: '50' },
    ],
  },
  HuaweiFC: {
    keyValues: [
      { key: 'cinder_huawei_conf_file', value: '/etc/cinder/cinder_huawei_conf.xml' },
      { key: 'hypermetro_devices', value: '' },
      { key: 'metro_domain_name', value: '' },
      { key: 'metro_san_address', value: '' },
      { key: 'metro_san_password', value: '', type: 'password' },
      { key: 'metro_san_user', value: '' },
      { key: 'metro_storage_pools', value: '' },
      { key: 'volumes_dir', value: DEFAULT_VOLUMES_DIR },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'image_volume_cache_max_count', value: '50' },
    ],
  },
  Custom: {
    keyValues: [],
  },
  HPE3PARFC: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: 'admin', required: true },
      { key: 'san_password', value: '', type: 'password', required: true },
      { key: 'hpe3par_api_url', value: '', required: true },
      { key: 'hpe3par_username', value: '', required: true },
      { key: 'hpe3par_password', value: '', type: 'password', required: true },
      { key: 'hpe3par_cpg', value: '[`OpenStack`]', required: true },
      { key: 'hpe3par_debug', value: 'false' },
      { key: 'target_ip_address', value: '' },
      { key: 'target_port', value: '3260' },
      { key: 'ssh_conn_timeout', value: '30' },
      { key: 'san_ssh_port', value: '22' },
      { key: 'unique_fqdn_network', value: 'true' },
      { key: 'volumes_dir', value: DEFAULT_VOLUMES_DIR },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20.0' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'enforce_multipath_for_image_xfer', value: 'true' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
      { key: 'hpe3par_cpg_snap', value: '' },
      { key: 'hpe3par_iscsi_chap_enabled', value: 'false' },
      { key: 'hpe3par_iscsi_ips', value: '' },
      { key: 'hpe3par_snapshot_expiration', value: '' },
      { key: 'hpe3par_snapshot_retention', value: '' },
      { key: 'hpe3par_target_nsp', value: '' },
      { key: 'replication_device', value: '' },
      { key: 'san_private_key', value: '' },
    ],
  },
  HPE3PARISCSI: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: 'admin', required: true },
      { key: 'san_password', value: '', type: 'password', required: true },
      { key: 'hpe3par_api_url', value: '', required: true },
      { key: 'hpe3par_username', value: '', required: true },
      { key: 'hpe3par_password', value: '', type: 'password', required: true },
      { key: 'hpe3par_cpg', value: '[`OpenStack`]', required: true },
      { key: 'hpe3par_iscsi_ips', value: '' },
      { key: 'hpe3par_debug', value: 'false' },
      { key: 'target_ip_address', value: '' },
      { key: 'target_port', value: '3260' },
      { key: 'ssh_conn_timeout', value: '30' },
      { key: 'san_ssh_port', value: '22' },
      { key: 'unique_fqdn_network', value: 'true' },
      { key: 'volumes_dir', value: DEFAULT_VOLUMES_DIR },
      { key: 'reserved_percentage', value: '0' },
      { key: 'max_over_subscription_ratio', value: '20.0' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
      { key: 'hpe3par_cpg_snap', value: '' },
      { key: 'hpe3par_iscsi_chap_enabled', value: 'false' },
      { key: 'hpe3par_snapshot_expiration', value: '' },
      { key: 'hpe3par_snapshot_retention', value: '' },
      { key: 'hpe3par_target_nsp', value: '' },
      { key: 'replication_device', value: '' },
      { key: 'san_private_key', value: '' },
    ],
  },
  HPENimbleISCSI: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', required: true, type: 'password' },
      { key: 'nimble_pool_name', value: 'default', required: true },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'nimble_subnet_label', value: '*' },
      { key: 'nimble_verify_certificate', value: 'false' },
      { key: 'nimble_verify_cert_path', value: '' },
      { key: 'san_thin_provision', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'image_volume_cache_max_count', value: '50' },
    ],
  },
  HPENimbleFC: {
    keyValues: [
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', required: true, type: 'password' },
      { key: 'nimble_pool_name', value: 'default', required: true },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'nimble_subnet_label', value: '*' },
      { key: 'nimble_verify_certificate', value: 'false' },
      { key: 'nimble_verify_cert_path', value: '' },
      { key: 'san_thin_provision', value: 'true' },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'image_volume_cache_max_count', value: '50' },
    ],
  },
  Tintri: {
    keyValues: [
      { key: 'nas_host', value: '', required: true },
      { key: 'nas_share_path', value: '/tintri/cinder', required: true },
      { key: 'vmstore_user', value: '', required: true },
      { key: 'vmstore_password', value: '', type: 'password', required: true },
      { key: 'vmstore_rest_address', value: '', required: true },
      { key: 'vmstore_qcow2_volumes', value: 'true' },
      { key: 'nfs_snapshot_support', value: 'true' },
      { key: 'nfs_mount_options', value: 'vers=3,proto=tcp,lookupcache=pos,nolock,noacl' },
      { key: 'volumes_dir', value: DEFAULT_VOLUMES_DIR },
      { key: 'image_volume_cache_enabled', value: 'true' },
      { key: 'image_volume_cache_max_count', value: '50' },
      { key: 'image_volume_cache_max_size_gb', value: '200' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
      { key: 'image_upload_use_cinder_backend', value: 'true' },
    ],
  },
  // PCD-1063: Dell-EMC templates. Unlike the dedicated cinder roles for NetApp/Pure,
  // Dell uses the generic "other" cinder role, which forwards volume_driver from the
  // saved config — so each Dell driver carries volume_driver as a fixed, read-only
  // field (the OpenStack-mandatory per-backend option that selects the driver). See
  // DellEmcStorageTemplates.spec.md (DELL-FR-003/004). Field sets per OpenStack docs.
  DellPowerStoreFC: {
    keyValues: [
      {
        key: 'volume_driver',
        value: 'cinder.volume.drivers.dell_emc.powerstore.driver.PowerStoreDriver',
        required: true,
        disabled: true,
      },
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', type: 'password', required: true },
      { key: 'storage_protocol', value: 'FC' },
      { key: 'powerstore_ports', value: '' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
    ],
  },
  DellPowerMaxFC: {
    keyValues: [
      {
        key: 'volume_driver',
        value: 'cinder.volume.drivers.dell_emc.powermax.fc.PowerMaxFCDriver',
        required: true,
        disabled: true,
      },
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', type: 'password', required: true },
      { key: 'powermax_array', value: '', required: true },
      { key: 'powermax_srp', value: 'SRP_1', required: true },
      { key: 'powermax_port_groups', value: '', required: true },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
    ],
  },
  DellUnityFC: {
    keyValues: [
      {
        key: 'volume_driver',
        value: 'cinder.volume.drivers.dell_emc.unity.Driver',
        required: true,
        disabled: true,
      },
      { key: 'storage_protocol', value: 'FC', required: true, disabled: true },
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', type: 'password', required: true },
      { key: 'unity_io_ports', value: '' },
      { key: 'volumes_dir', value: DEFAULT_VOLUMES_DIR },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
    ],
  },
  DellSCISCSI: {
    keyValues: [
      {
        key: 'volume_driver',
        value: 'cinder.volume.drivers.dell_emc.sc.storagecenter_iscsi.SCISCSIDriver',
        required: true,
        disabled: true,
      },
      { key: 'san_ip', value: '', required: true },
      { key: 'san_login', value: '', required: true },
      { key: 'san_password', value: '', type: 'password', required: true },
      { key: 'dell_sc_ssn', value: '', required: true },
      { key: 'dell_sc_api_port', value: '3033' },
      { key: 'dell_sc_volume_folder', value: '' },
      { key: 'dell_sc_server_folder', value: '' },
      { key: 'use_multipath_for_image_xfer', value: 'true' },
    ],
  },
}

export const getPairValue = (
  name: string,
): { nfs_shares_config: string; volume_backend_name: string } => {
  return {
    // PCD-2830 Allow users to configure nfs_shares_config, that line is irrelevant now
    nfs_shares_config: '/opt/pf9/etc/pf9-cindervolume-base/conf.d/nfs_shares',
    volume_backend_name: name || 'NA',
  }
}

export const DRIVERS = [
  'NFS',
  'LVM',
  'NetAppONTAPNFS',
  'NetAppONTAPFC',
  'NetAppONTAPISCSI',
  'NetAppONTAPNVMe',
  'PureStorageISCSI',
  'PureStorageFC',
  'PureStorageNVME',
  'HitachiISCSI',
  'HitachiFC',
  'InfinidatISCSI',
  'InfinidatFC',
  'HuaweiISCSI',
  'HuaweiFC',
  'Custom',
  'HPE3PARISCSI',
  'HPE3PARFC',
  'HPENimbleISCSI',
  'HPENimbleFC',
  'Tintri',
  'DellPowerStoreFC',
  'DellPowerMaxFC',
  'DellUnityFC',
  'DellSCISCSI',
]

// For data manipulation, should recognize if driver === 'Custom' for
// blueprint -> API body reason and is outside of DRIVERS list for
// API body -> blueprint reason
export const isCustomDriver = (driver) => {
  return driver === 'Custom' || !DRIVERS.includes(driver)
}

export const stringToBoolean = (str) => {
  if (str?.toLowerCase() === 'true') {
    return true
  } else if (str?.toLowerCase() === 'false') {
    return false
  }
  return null
}

// Update key to title case by replacing _ with space and capitalizing first letter of each word
export const convertToTitleCase = (key: any): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/Nfs/g, 'NFS')
    .replace(/Netapp/g, 'NetApp')
}
